// @flow
import React from 'react';
import { showServerError } from 'bk-web-app';
import { toastError, toastSuccess } from 'bench-internal-theme';
import AddonsTabRow from './AddonsTabRow';
import type { Addon } from '../types';
import { DELETE_ADDON, GET_ADDONS } from '../queries';
import { useDrop } from 'react-dnd';
import { useClient } from '../../ClientContext';
import { useMutation } from '@apollo/client';
import EmptyTableRow from '../EmptyTableRow';

type AddonTabRowContainerProps = {
  addon: Addon,
  search: string,
  changeOrder: (string, string, boolean) => void,
  isSorting: boolean,
  isSortSaving: boolean,
  canSort: boolean,
};

const AddonTabRowContainer = ({
  addon,
  search,
  changeOrder,
  isSorting,
  isSortSaving,
  canSort,
}: AddonTabRowContainerProps) => {
  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'Add-on',
      canDrop: monitor => monitor.id !== addon.id && isSorting && canSort,
      drop: monitor => changeOrder(monitor.id, addon.id, false),
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [isSorting, canSort],
  );

  const client = useClient();
  const [deleteAddon] = useMutation(DELETE_ADDON, {
    onCompleted: () => {
      toastSuccess('Add-on Deleted', 'Your add-on has been deleted.');
    },
    onError: error => {
      toastError('An error occurred deleting this add-on.', showServerError(error));
    },
    refetchQueries: [{ query: GET_ADDONS, variables: { clientId: client.id } }],
  });

  return (
    <div ref={drop}>
      {isSorting && canSort && canDrop && isOver && <EmptyTableRow isOver={isOver} />}
      <AddonsTabRow
        deleteAddon={() => deleteAddon({ variables: { addonId: addon.id } })}
        addon={addon}
        search={search}
        isSorting={isSorting}
        isSortSaving={isSortSaving}
        canSort={canSort}
      />
    </div>
  );
};

export default AddonTabRowContainer;
